import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { usePosCart } from "@/hooks/pos/use-cart";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { convertUsdToSech } from "@/lib/pos/sech-utils";
import { Plus, Minus, Loader2, ChevronLeft, Trash, Flame, Leaf, Flag, X, Tag } from "lucide-react";
import raw from "@/assets/pos/noto_root-vegetable.svg";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { UserData } from "@/types/pos";
import { Label } from "@/components/ui/label";
import PaymentMethodSelector from "@/components/PaymentMethodSelector";
import type { SelectablePaymentMethod } from "@/types/payment-methods";
import { DEFAULT_PAYMENT_METHOD } from "@/types/payment-methods";

interface TaxCalculationResult {
	subtotal: number;
	fees: Array<{
		type: string;
		rate: number;
		amount: number;
		description: string;
	}>;
	total: number;
}

interface CartModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export default function CartModal({ isOpen, onClose }: CartModalProps) {
	const {
		cartItems,
		updateQuantity,
		removeFromCart,
		getCartTotal,
		sechUser,
		requestPayment,
		isPaymentProcessing,
		// Discount-related
		appliedDiscount,
		promoCode,
		setPromoCode,
		applyPromoCode,
		removeDiscount,
		isValidatingPromo,
		promoError,
		getDiscountedTotal,
		refreshDiscount,
	} = usePosCart();
	const { sechPrice } = useSechPrice();
	const { toast } = useToast();

	// Get tax calculation from cart context (which already applies discount)
	const { taxCalculation, isCalculatingTaxes } = usePosCart();
	const [userData, setUserData] = useState<UserData | null>(null);
	const [itemToDelete, setItemToDelete] = useState<string | null>(null);
	const [orderNotes, setOrderNotes] = useState<string>("");
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<SelectablePaymentMethod>(DEFAULT_PAYMENT_METHOD);
	const prevPaymentType = useRef(selectedPaymentMethod.type);

	const isCardPayment = selectedPaymentMethod.type === 'CARD';

	// Re-validate discount when payment method changes
	useEffect(() => {
		const changed = prevPaymentType.current !== selectedPaymentMethod.type;
		prevPaymentType.current = selectedPaymentMethod.type;
		if (!changed || !isOpen) return;
		refreshDiscount(selectedPaymentMethod.type);
	}, [selectedPaymentMethod.type, isOpen, refreshDiscount]);

	const cartTotal = getCartTotal();
	const discountedTotal = getDiscountedTotal();

	// Set user data when modal opens
	useEffect(() => {
		if (isOpen && sechUser) {
			setUserData({
				id: sechUser.uid,
				pickupPerson: sechUser.displayName
			});
		}
	}, [isOpen, sechUser]);

	const handleCheckout = () => {
		if (cartItems.length === 0) return;

		if (sechUser && requestPayment) {
			requestPayment(undefined, userData!, orderNotes.trim() || undefined, selectedPaymentMethod);
		}
	};

	const totalWithFees = taxCalculation ? taxCalculation.total : cartTotal;

	// Check if user has sufficient SECH balance
	const hasInsufficientBalance =
		selectedPaymentMethod.type === 'TOKEN' &&
		sechUser &&
		sechPrice &&
		convertUsdToSech(totalWithFees, sechPrice) > sechUser.balance;

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(2);
	};

	const formatAmount = (usdAmount: number) => {
		if (isCardPayment) return `$${formatPrice(usdAmount)}`;
		if (!sechPrice) return `$${formatPrice(usdAmount)}`;
		return `${convertUsdToSech(usdAmount, sechPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`;
	};

	const handleDeleteConfirm = () => {
		if (itemToDelete) {
			removeFromCart(itemToDelete);
			setItemToDelete(null);
			if (cartItems.length === 1) {
				onClose();
			}
		}
	};

	return (
		<div className="p-5 bg-[var(--pos-secondary-background-color)] h-auto min-h-auto flex flex-col max-w-5xl mx-auto pt-[56px]">
			<div className="flex-1 overflow-hidden flex flex-col min-h-0">
				<div className="fixed top-0 left-0 right-0 z-50 bg-[var(--pos-secondary-background-color)]">
					<div className="max-w-5xl mx-auto flex items-center gap-3 px-5 py-3">
						<button className="h-8 w-8 rounded-full flex items-center justify-center bg-[var(--pos-primary-background-color)]" onClick={() => onClose()}>
							<ChevronLeft className="w-5 h-5 text-[var(--pos-primary-text-color)]" />
						</button>
						<p className="font-montserrat text-xl leading-5 text-[var(--pos-primary-text-color)] font-bold">
							Your Order
						</p>
					</div>
				</div>

				{sechUser && (
					<div className="flex-shrink-0">
						<div className="flex items-center justify-between mb-3">
							<p className="text-sm font-montserrat leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-0">
								SECH Balance
							</p>
							<span className="h-8 bg-[--pos-sech-count-bg-color] text-[#258750] font-figtree flex items-center justify-center rounded-lg px-2 text-xs leading-4 font-medium">
								{sechUser.balance.toLocaleString()} SECH
							</span>
						</div>

						<div className="rounded-lg px-2 py-3 mb-1 bg-[var(--pos-primary-background-color)]">
							<p className="font-montserrat text-xs leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-1">
								Pickup Person Name
							</p>
							<p className="font-figtree text-xs leading-4 font-medium text-[var(--pos-secondary-text-color)] mb-3">
								<span className="text-[var(--pos-primary-color)]">Note: </span> This will be
								displayed to the staff when you pick up your order.
							</p>

							<div className="flex flex-col">
								<Label className="text-xs font-medium leading-[15px] text-[var(--pos-primary-text-color)] mb-2">
									Name
								</Label>

								<Input
									placeholder="Enter Menu Name"
									type="text"
									className="rounded-sm bg-[var(--pos-input-background-color)] text-[var(--pos-primary-text-color)] w-full px-3 h-[38px] border border-[var(--pos-input-border-color)] text-xs leading-[22px] font-normal placeholder:text-xs placeholder:leading-[22px] placeholder:font-normal placeholder:text-[#676879] focus-visible:outline-none focus-visible:ring-0"
									name="displayName"
									value={userData?.pickupPerson ?? ""}
									onChange={(e) =>
										setUserData({
											id: sechUser?.uid || "",
											pickupPerson: e.target.value,
										})
									}
								/>
							</div>
						</div>

						{/* Special Instructions / Order Notes */}
						<div className="rounded-lg px-2 py-3 mt-1 bg-[var(--pos-primary-background-color)]">
							<p className="font-montserrat text-xs leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-1">
								Special Instructions
							</p>
							<p className="font-figtree text-xs leading-4 font-medium text-[var(--pos-secondary-text-color)] mb-3">
								<span className="text-[var(--pos-primary-color)]">Optional: </span> Add any special requests or dietary notes for your order.
							</p>

							<textarea
								placeholder="e.g., No onions, extra spicy, allergies..."
								className="rounded-sm bg-[var(--pos-input-background-color)] text-[var(--pos-primary-text-color)] w-full px-3 py-2 border border-[var(--pos-input-border-color)] text-xs leading-[22px] font-normal placeholder:text-xs placeholder:leading-[22px] placeholder:font-normal placeholder:text-[#676879] focus-visible:outline-none focus-visible:ring-0 resize-none"
								rows={3}
								maxLength={500}
								value={orderNotes}
								onChange={(e) => setOrderNotes(e.target.value)}
							/>
							<p className="text-right text-xs text-[var(--pos-secondary-text-color)] mt-1">
								{orderNotes.length}/500
							</p>
						</div>
					</div>
				)}

				{!cartItems?.length ? (
					<div className="text-center py-8 flex-1">
						<p className="text-gray-500">Your cart is empty</p>
					</div>
				) : (
					<div className="flex-1 overflow-y-auto min-h-0">
						{cartItems.map((item) => {
							const imageSrc = item.product?.imageUrl
							|| item.customDrink?.components?.find((c) => c.type === "spirit" && c.imageUrl)?.imageUrl
							|| null;
							return (
							<div
								key={item.id}
								className="rounded-lg px-2 py-3 mb-1 last:mb-0 bg-[var(--pos-primary-background-color)]"
							>
								<div className="flex items-center justify-between border-b border-dashed border-[#C3C6D4] pb-3 mb-3">

									<div className="flex gap-2">
										{imageSrc && (
											<img
												src={imageSrc}
												alt={item?.product?.name || item?.customDrink?.name}
												className="w-12 h-12 rounded-lg object-cover"
												onError={(e) => {
													e.currentTarget.style.display = 'none';
												}}
											/>
										)}
										<div className="flex flex-col">
											<div className="flex items-center gap-1">
												<p className="font-montserrat text-sm leading-[28px] font-semibold text-[var(--pos-primary-text-color)]">
													{item.product?.name || item.customDrink?.name}
												</p>
												{item.product?.isSpicy && (
													<Flame className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
												)}
												{item.product?.isVegan && (
													<Leaf className="w-4 h-4 text-[#22C55E] fill-[#22C55E]" />
												)}
												{item.product?.isRaw && (
													<img src={raw} alt="Raw" className="w-4 h-4" />
												)}
												{item.product?.isSignature && (
													<Flag className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
												)}
											</div>
											<p className="font-figtree mb-0 text-sm leading-[18px] font-bold text-[var(--pos-primary-text-color)]">
												{formatAmount(item.unitPriceUsd * item.quantity)}
											</p>
										</div>


									</div>
								</div>

								<div className="flex justify-between items-center gap-5">
									<div className="flex-1 min-w-0">
										{item.customDrink && (
											<p className="text-xs text-[var(--pos-secondary-text-color)]">
												{item.customDrink.components
													.map((c) => c.name)
													.join(", ")}
											</p>
										)}

										{item.selectedModifiers && item.selectedModifiers.length > 0 && (
											<div className="mb-1">
												{item.selectedModifiers.map((mod, idx) => (
													<span key={mod.modifierId} className="text-xs text-[var(--pos-secondary-text-color)]">
														{mod.modifierName}
														{mod.priceAdjustmentUsd !== 0 && (
															<span className="text-[var(--pos-primary-color)]">
																{mod.priceAdjustmentUsd > 0 ? ` (+${formatAmount(mod.priceAdjustmentUsd)})` : ` (-${formatAmount(Math.abs(mod.priceAdjustmentUsd))})`}
															</span>
														)}
														{idx < item.selectedModifiers!.length - 1 ? ", " : ""}
													</span>
												))}
											</div>
										)}

										<p className="font-montserrat text-xs leading-4 font-medium text-[var(--pos-secondary-text-color)]">
											{item?.product?.description ?? ""}
										</p>
									</div>



									<div className="flex items-center gap-3">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setItemToDelete(item.id)}
											className="h-7 w-7 p-0 rounded-full bg-[--pos-delete-btn-bg-color] flex items-center justify-center"
											disabled={isPaymentProcessing || isCalculatingTaxes}
										>
											<Trash className="h-4 w-4 text-[var(--pos-primary-color)]" />
										</Button>

										<button
											onClick={() => {
												if (item.quantity <= 1) {
													setItemToDelete(item.id);
												} else {
													updateQuantity(item.id, item.quantity - 1);
												}
											}}
											disabled={isPaymentProcessing || isCalculatingTaxes}
											className={`h-7 w-7 border-0 flex items-center justify-center rounded-full bg-[--pos-accordian-enabled-btn-background] ${isPaymentProcessing || isCalculatingTaxes ? "disabled-count-btn" : ""}`}
										>
											<Minus className="icon h-[18px] w-[18px] text-[--pos-accordian-enabled-btn-color]" />
										</button>

										<span className="font-montserrat text-sm leading-[18px] font-semibold text-[var(--pos-primary-text-color)] w-[22px] text-center">
											{item.quantity}
										</span>

										<button
											onClick={() => updateQuantity(item.id, item.quantity + 1)}
											disabled={isPaymentProcessing || isCalculatingTaxes}
											className={`h-7 w-7 border-0 flex items-center justify-center rounded-full bg-[--pos-accordian-enabled-btn-background] ${isPaymentProcessing || isCalculatingTaxes ? "disabled-count-btn" : ""}`}
										>
											<Plus className="icon h-[18px] w-[18px] text-[--pos-accordian-enabled-btn-color]" />
										</button>
									</div>
								</div>
							</div>
							)
						})}
					</div>
				)}
			</div>

			{/* FOOTER SECTION */}
			{cartItems.length > 0 && (
				<div className="flex-shrink-0 pt-3 pb-10">
					{/* Promo Code Section - always visible so user can enter a different code */}
					<div className="p-3 rounded-lg bg-[var(--pos-primary-background-color)] mb-3">
						<div className="flex gap-2">
							<div className="flex-1">
								<Input
									placeholder="Enter promo code"
									type="text"
									className="rounded-sm bg-[var(--pos-input-background-color)] text-[var(--pos-primary-text-color)] w-full px-3 h-[38px] border border-[var(--pos-input-border-color)] text-xs leading-[22px] font-normal placeholder:text-xs placeholder:leading-[22px] placeholder:font-normal placeholder:text-[#676879] focus-visible:outline-none focus-visible:ring-0 uppercase"
									value={promoCode}
									onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
									disabled={isValidatingPromo || isPaymentProcessing}
								/>
								{promoError && (
									<p className="text-xs text-[var(--pos-primary-color)] mt-1">{promoError}</p>
								)}
							</div>
							<Button
								onClick={() => applyPromoCode(selectedPaymentMethod.type)}
								disabled={isValidatingPromo || isPaymentProcessing || !promoCode.trim()}
								className="h-[38px] px-4 bg-[var(--pos-primary-color)] text-white text-xs font-medium"
							>
								{isValidatingPromo ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									"Apply"
								)}
							</Button>
						</div>
					</div>

					{/* Applied Discount Badge */}
					{appliedDiscount && (
						<div className="p-3 rounded-lg bg-[#F0FDF4] border border-[#22C55E] mb-3">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<Tag className="h-4 w-4 text-[#22C55E]" />
									<div>
										<p className="font-montserrat text-xs font-semibold text-[#166534]">
											{appliedDiscount.discountName}
										</p>
										<p className="text-xs text-[#15803D]">
											{appliedDiscount.discountType === 'percentage'
												? `${appliedDiscount.discountValue}% off`
												: `$${appliedDiscount.discountValue.toFixed(2)} off`}
											{appliedDiscount.promoCode && ` (${appliedDiscount.promoCode})`}
											{appliedDiscount.maxDiscountAmount && appliedDiscount.calculatedAmount >= appliedDiscount.maxDiscountAmount && (
												<span className="text-[#166534]"> - Max ${appliedDiscount.maxDiscountAmount.toFixed(2)}</span>
											)}
										</p>
									</div>
								</div>
{/* Only show X button for manually applied promos, not auto-apply discounts */}
								{appliedDiscount.isAutoApply === false && (
									<button
										onClick={removeDiscount}
										disabled={isPaymentProcessing}
										className="h-6 w-6 rounded-full bg-[#DCF4E4] flex items-center justify-center hover:bg-[#BBF7D0]"
									>
										<X className="h-3 w-3 text-[#166534]" />
									</button>
								)}
							</div>
						</div>
					)}

					{/* Payment Method Selection */}
					<div className="p-3 rounded-lg bg-[var(--pos-primary-background-color)] mb-3">
						<PaymentMethodSelector
							selectedMethod={selectedPaymentMethod}
							onSelect={setSelectedPaymentMethod}
						/>
					</div>

					{isCalculatingTaxes || !taxCalculation ? (
						<div className="flex items-center justify-center py-4 mb-4">
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
							<span className="text-sm text-[var(--pos-secondary-text-color)]">
								Calculating taxes and fees...
							</span>
						</div>
					) : (
						<>
							<div className="p-3 rounded-lg bg-[var(--pos-primary-background-color)] mb-3">
								{/* Show original subtotal */}
								<div className="flex items-center justify-between pb-2">
									<span className="font-montserrat text-[var(--pos-secondary-text-color)] text-[13px] leading-4 font-medium">
										Subtotal
									</span>
									<span className="font-montserrat text-[var(--pos-secondary-text-color)] text-[13px] leading-4 font-medium">
										{formatAmount(cartTotal)}
									</span>
								</div>

								{/* Show discount if applied */}
								{appliedDiscount && (
									<div className="flex items-center justify-between pb-2">
										<span className="font-montserrat text-[#22C55E] text-[13px] leading-4 font-medium">
											Discount
										</span>
										<span className="font-montserrat text-[#22C55E] text-[13px] leading-4 font-medium">
											-{formatAmount(appliedDiscount.calculatedAmount)}
										</span>
									</div>
								)}

								<div className="border-b border-dashed border-[#C3C6D4] mb-3" />

								{taxCalculation.fees?.map((fee, index) => (
									<div key={index} className="flex justify-between text-sm">
										<span className="text-[var(--pos-secondary-text-color)]">
											{(fee.rate * 100).toFixed(0)}% {fee.type?.replace(/[_-]+/g, " ")?.toLowerCase()?.replace(/\b\w/g, (char: string) => char.toUpperCase())}
										</span>
										<span className="text-[var(--pos-secondary-text-color)]">
											{formatAmount(fee.amount)}
										</span>
									</div>
								))}

								<div className="flex justify-between items-start">
									<p className="font-figtree text-sm leading-[18px] font-bold text-[var(--pos-primary-text-color)]">
										Total
									</p>

									<p className="font-figtree text-sm leading-[18px] font-bold text-end text-[var(--pos-primary-text-color)]">
										{formatAmount(taxCalculation.total)}
									</p>
								</div>
							</div>

							{hasInsufficientBalance && (
								<div className="bg-transparent border-0 rounded-lg py-x">
									<p className="text-xs leading-[18px] text-center font-medium font-figtree text-[var(--pos-primary-color)] mb-3">
										Insufficient SECH balance. Please add funds to your wallet.
									</p>
								</div>
							)}

							<Button
								onClick={handleCheckout}
								className="font-figtree rounded-lg w-full bg-[--pos-primary-color] flex items-center justify-center h-[42px] text-[#FFFFFF] text-sm leading-4 font-semibold"
								size="lg"
								disabled={
									isPaymentProcessing ||
									hasInsufficientBalance ||
									isCalculatingTaxes ||
									!taxCalculation
								}
							>
								{isPaymentProcessing ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Processing Payment...
									</>
								) : isCalculatingTaxes ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Calculating Taxes...
									</>
								) : sechUser ? (
									selectedPaymentMethod.type === 'CARD' ? "Pay with Card" : "Pay with SECH Wallet"
								) : (
									"Checkout"
								)}
							</Button>
						</>
					)}
				</div>
			)}

			{/* Delete Confirmation Dialog */}
			{itemToDelete && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-[var(--pos-primary-background-color)] rounded-lg p-4 max-w-[300px] w-full">
						<p className="font-montserrat text-sm font-semibold text-[var(--pos-primary-text-color)] mb-2">
							Remove Item?
						</p>
						<p className="font-figtree text-xs text-[var(--pos-secondary-text-color)] mb-4">
							Are you sure you want to remove this item from your order?
						</p>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setItemToDelete(null)}
								className="flex-1 h-9 font-figtree text-xs font-medium border-[var(--pos-input-border-color)] text-[var(--pos-primary-text-color)]"
							>
								Cancel
							</Button>
							<Button
								size="sm"
								onClick={handleDeleteConfirm}
								className="flex-1 h-9 font-figtree text-xs font-medium bg-[var(--pos-primary-color)] text-white"
							>
								Remove
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
